import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  User,
  FileText,
  CheckCircle2,
} from "lucide-react";

/**
 * DoctorCalendarView
 *
 * Props:
 * - bookings: Array<{
 *     id: string,
 *     scheduledDate: "YYYY-MM-DD",
 *     scheduledTime?: "HH:mm" | "HH:mm:ss",
 *     status?: "pending"|"confirmed"|"completed"|"cancelled",
 *     patientName?: string,
 *     service?: string,
 *     notes?: string,
 *     createdAt?: Date | Firestore TS-like
 *   }>
 * - onOpenMedicalNotes?: (booking) => void
 * - onUpdateAppointmentStatus?: (id: string, status: "confirmed"|"cancelled"|"completed", cancelReason?: string) => void
 */

const statusTheme = {
  confirmed: { bg: "#dcfce7", fg: "#166534" },
  pending:   { bg: "#fef3c7", fg: "#92400e" },
  completed: { bg: "#dbeafe", fg: "#1e40af" },
  cancelled: { bg: "#fee2e2", fg: "#991b1b" },
  default:   { bg: "#f3f4f6", fg: "#374151" },
};
const dotColor = (s) =>
  s === "confirmed" ? "#16a34a" :
  s === "pending"   ? "#d97706" :
  s === "completed" ? "#2563eb" :
  s === "cancelled" ? "#dc2626" : "#6b7280";

const prettyTime = (t) => {
  if (!t) return "Time TBD";
  try {
    return new Date(`2000-01-01T${t}`).toLocaleTimeString(undefined, {
      hour: "numeric", minute: "2-digit",
    });
  } catch { return t; }
};
const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth   = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

const buildMonthGrid = (activeDate) => {
  const start = startOfMonth(activeDate);
  const end = endOfMonth(activeDate);
  const startWeekday = start.getDay();
  const daysInMonth = end.getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() - (startWeekday - i));
    cells.push({ date: d, inMonth: false });
  }
  for (let i = 1; i <= daysInMonth; i++) {
    cells.push({ date: new Date(activeDate.getFullYear(), activeDate.getMonth(), i), inMonth: true });
  }
  while (cells.length < 42) {
    const last = cells[cells.length - 1].date;
    const d = new Date(last);
    d.setDate(d.getDate() + 1);
    cells.push({ date: d, inMonth: d.getMonth() === activeDate.getMonth() });
  }
  return cells;
};

const groupByDate = (bookings = []) => {
  const map = new Map();
  bookings.forEach((b) => {
    const key = b.scheduledDate;
    if (!key) return;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(b);
  });
  return map;
};

/* ---------- Mobile helpers: 3-day window ---------- */
const addDays = (iso, n) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return toISO(d);
};
const threeDayWindow = (centerISO) => [
  addDays(centerISO, -1),
  centerISO,
  addDays(centerISO, 1),
];

/* ---------- Component ---------- */
const DoctorCalendarView = ({
  bookings = [],
  onOpenMedicalNotes,
  onUpdateAppointmentStatus = () => {},
}) => {
  const today = useMemo(() => new Date(), []);
  const [activeMonth, setActiveMonth] = useState(
    () => new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const [selectedDateISO, setSelectedDateISO] = useState(() => toISO(today));

  // NEW: decline modal state
  const [declineBooking, setDeclineBooking] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [declineError, setDeclineError] = useState("");

  // derived
  const monthLabel = activeMonth.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
  const grid = useMemo(() => buildMonthGrid(activeMonth), [activeMonth]);
  const groupedAll = useMemo(() => groupByDate(bookings), [bookings]);

  // keyboard nav (desktop)
  const gridRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (!gridRef.current || !gridRef.current.contains(document.activeElement))
        return;
      let delta = 0;
      if (e.key === "ArrowLeft") delta = -1;
      else if (e.key === "ArrowRight") delta = 1;
      else if (e.key === "ArrowUp") delta = -7;
      else if (e.key === "ArrowDown") delta = 7;
      else return;
      e.preventDefault();
      const d = new Date(selectedDateISO);
      d.setDate(d.getDate() + delta);
      const newMonth = new Date(d.getFullYear(), d.getMonth(), 1);
      const mk1 = activeMonth.getFullYear() + "-" + activeMonth.getMonth();
      const mk2 = newMonth.getFullYear() + "-" + newMonth.getMonth();
      if (mk1 !== mk2) setActiveMonth(newMonth);
      setSelectedDateISO(toISO(d));
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [selectedDateISO, activeMonth]);

  const goPrevMonth = () =>
    setActiveMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  const goNextMonth = () =>
    setActiveMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  const goToday = () => {
    const t = new Date();
    setActiveMonth(new Date(t.getFullYear(), t.getMonth(), 1));
    setSelectedDateISO(toISO(t));
    setTimeout(() => gridRef.current?.focus(), 0);
  };

  // mobile three-day strip
  const mobileDays = threeDayWindow(selectedDateISO);

  // Helpers
  const agendaFor = (iso) =>
    (groupedAll.get(iso) || [])
      .slice()
      .sort((a, b) => (a.scheduledTime || "").localeCompare(b.scheduledTime || ""));

  // NEW: decline handlers
  const handleConfirmDecline = () => {
    if (!declineBooking) return;

    const reason = declineReason.trim();
    if (!reason) {
      setDeclineError("Please write a short reason for declining.");
      return;
    }

    // send reason as 3rd arg
    onUpdateAppointmentStatus(declineBooking.id, "cancelled", reason);

    setDeclineBooking(null);
    setDeclineReason("");
    setDeclineError("");
  };

  const handleCancelDecline = () => {
    setDeclineBooking(null);
    setDeclineReason("");
    setDeclineError("");
  };

  const renderActions = (a) => {
    if (a.status === "pending") {
      return (
        <div className="dcv-actions">
          <button
            type="button"
            className="dcv-btn dcv-btn--ok"
            onClick={() => onUpdateAppointmentStatus(a.id, "confirmed")}
            title="Confirm appointment"
          >
            ✓ Confirm
          </button>
          <button
            type="button"
            className="dcv-btn dcv-btn--danger"
            onClick={() => {
              setDeclineBooking(a);
              setDeclineReason("");
              setDeclineError("");
            }}
            title="Decline appointment"
          >
            ✗ Decline
          </button>
        </div>
      );
    }
    if (a.status === "confirmed") {
      return (
        <button
          type="button"
          className="dcv-btn dcv-btn--primary"
          onClick={() => onOpenMedicalNotes?.(a)}
          title="Fill medical notes"
        >
          <CheckCircle2 size={12} /> Medical Notes
        </button>
      );
    }
    // cancelled or completed → no actions
    return null;
  };

  return (
    <section className="dcv-root">
      <style>{`
        .dcv-root {
          --border:#e5e7eb; --muted:#6b7280; --bg:#fff; --soft:#f8fafc;
          --radius:14px; --shadow:0 12px 26px -18px rgba(0,0,0,.22);
          --brand:#0a66c2;
        }
        .dcv-wrap { display:grid; grid-template-columns: 1.6fr 1fr; gap:16px; }
        @media (max-width: 860px){ .dcv-wrap { grid-template-columns:1fr; } }

        .dcv-card { background:var(--bg); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow); overflow:hidden; }

        .dcv-head { display:flex; align-items:center; justify-content:space-between; padding:12px 14px; background:#f9fafb; border-bottom:1px solid var(--border); }
        .dcv-title { display:flex; align-items:center; gap:10px; font-weight:800; color:#0f172a; font-size:15px; margin:0; }
        .dcv-controls { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
        .dcv-btnnav { display:grid; place-items:center; width:32px; height:32px; border-radius:10px; border:1px solid var(--border); background:#fff; color:#374151; cursor:pointer; }
        .dcv-today { font-weight:800; font-size:12.5px; padding:6px 10px; border-radius:10px; border:1px solid var(--border); background:#fff; cursor:pointer; }

        /* Desktop month grid */
        .dcv-grid { display:grid; grid-template-columns: repeat(7, 1fr); border-top:1px solid var(--border); }
        .dcv-dow { text-transform:uppercase; font-size:11px; font-weight:800; color:var(--muted); letter-spacing:.06em; padding:10px 8px; background:#fbfbfb; border-bottom:1px solid var(--border); }
        .dcv-cell { min-height:100px; border-right:1px solid var(--border); border-bottom:1px solid var(--border); padding:8px; background:#fff; cursor:pointer; }
        .dcv-cell:nth-child(7n){ border-right:none; }
        .dcv-cell--out{ background:#fafafa; color:#9ca3af; }
        .dcv-cell--today{ box-shadow: inset 0 0 0 2px #0ea5e9; background:#f0f9ff; }
        .dcv-cell--sel{ outline:2px solid rgba(37,99,235,.35); outline-offset:-2px; }
        .dcv-daybar{ display:flex; align-items:center; justify-content:space-between; gap:8px; }
        .dcv-daynum{ font-size:12px; font-weight:900; color:#111827; }
        .dcv-daynum--out{ color:#9ca3af; }
        .dcv-count{ font-size:10px; font-weight:800; padding:2px 6px; border-radius:999px; background:#eef2ff; color:#3730a3; border:1px solid #e0e7ff; }
        .dcv-apts{ margin-top:6px; display:flex; flex-direction:column; gap:4px; }
        .dcv-chip{ display:inline-flex; align-items:center; gap:6px; max-width:100%; padding:4px 6px; border-radius:8px; font-size:11.5px; font-weight:700; border:1px solid rgba(0,0,0,0.05); overflow:hidden; white-space:nowrap; text-overflow:ellipsis; }
        .dcv-dot{ width:7px; height:7px; border-radius:999px; flex:0 0 7px; }
        .dcv-more{ font-size:11px; color:#2563eb; font-weight:800; cursor:pointer; }

        /* Right agenda */
        .dcv-list{ padding:10px 10px 14px; display:grid; gap:10px; }
        .dcv-empty{ text-align:center; padding:28px; color:#64748b; }
        .dcv-item{ display:flex; gap:10px; border:1px solid var(--border); border-radius:12px; padding:10px 12px; background:#fff; }
        .dcv-avatar{ width:36px; height:36px; border-radius:10px; display:grid; place-items:center; color:#fff; background:var(--brand); }
        .dcv-name{ margin:0; font-size:14px; font-weight:800; color:#0f172a; }
        .dcv-meta{ display:flex; gap:8px; flex-wrap:wrap; margin-top:4px; }
        .dcv-chip2{ display:inline-flex; align-items:center; gap:6px; font-size:11.5px; font-weight:700; background:#f3f4f6; border:1px solid #e5e7eb; border-radius:999px; padding:4px 8px; color:#334155; }
        .dcv-badge{ font-size:11.5px; font-weight:800; padding:3px 8px; border-radius:999px; border:1px solid transparent; }
        .dcv-notes{ margin-top:6px; font-size:12.5px; color:#657084; background:#fafafa; border:1px dashed #eaeaea; padding:8px 10px; border-radius:8px; }

        /* Action buttons */
        .dcv-actions { display:flex; gap:8px; align-items:center; margin-left:auto; }
        .dcv-btn {
          display:inline-flex; align-items:center; gap:6px; cursor:pointer; border:none; border-radius:8px;
          padding:8px 10px; font-size:12.5px; font-weight:800; color:#fff;
        }
        .dcv-btn--primary { background:#10b981; }
        .dcv-btn--ok { background:#10b981; }
        .dcv-btn--danger { background:#ef4444; }

        /* ===== Mobile: hide month grid, show 3-day strip ===== */
        @media (max-width: 640px){
          .dcv-desktop { display:none; }
          .dcv-wrap { gap:12px; }
        }
        @media (min-width: 641px){
          .dcv-mobile { display:none; }
        }

        .mbar { display:flex; align-items:center; justify-content:space-between; padding:10px 12px; border-bottom:1px solid var(--border); background:#f9fafb; }
        .mnav { display:flex; align-items:center; gap:6px; }
        .mnav-btn { width:32px; height:32px; display:grid; place-items:center; border:1px solid var(--border); border-radius:10px; background:#fff; }
        .mtrip { display:flex; gap:8px; padding:12px; }
        .mdot { width:6px; height:6px; border-radius:999px; }
        .mcard {
          flex:1 1 0; border:1px solid var(--border); border-radius:12px; background:#fff; padding:10px;
          display:flex; flex-direction:column; gap:6px; align-items:flex-start; cursor:pointer;
        }
        .mcard--sel { outline:2px solid rgba(37,99,235,.35); outline-offset:-2px; }
        .mdate { font-size:12px; font-weight:800; color:#0f172a; }
        .mcount { font-size:10.5px; font-weight:800; padding:2px 6px; border-radius:999px; background:#eef2ff; color:#3730a3; border:1px solid #e0e7ff; }
        .mlegend { display:flex; gap:4px; }
        .mfoot { padding:0 12px 12px; color:#64748b; font-size:12px; }

        /* Decline modal */
        .dcv-modalOverlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.55);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9998;
        }

        .dcv-modal {
          background: #ffffff;
          border-radius: 16px;
          padding: 18px 18px 14px;
          max-width: 420px;
          width: 100%;
          box-shadow: 0 18px 45px rgba(0, 0, 0, 0.35);
        }

        .dcv-modalTitle {
          margin: 0 0 4px 0;
          font-size: 16px;
          font-weight: 700;
          color: #111827;
        }

        .dcv-modalSubtitle {
          margin: 0 0 12px 0;
          font-size: 12.5px;
          color: #6b7280;
        }

        .dcv-modalBody {
          margin-bottom: 12px;
        }

        .dcv-modalLabel {
          font-size: 12px;
          color: #4b5563;
          margin-bottom: 6px;
        }

        .dcv-modalTextarea {
          width: 100%;
          min-height: 90px;
          padding: 8px 10px;
          border-radius: 10px;
          border: 1px solid #d1d5db;
          font-size: 13px;
          resize: vertical;
          box-sizing: border-box;
        }

        .dcv-modalTextarea:focus {
          outline: none;
          border-color: #dc2626;
          box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.25);
        }

        .dcv-modalError {
          margin-top: 6px;
          font-size: 11.5px;
          color: #b91c1c;
        }

        .dcv-modalActions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }

        .dcv-modalBtn {
          border-radius: 999px;
          border: none;
          padding: 7px 14px;
          font-size: 12.5px;
          cursor: pointer;
          font-weight: 600;
        }

        .dcv-modalBtn--secondary {
          background: #f3f4f6;
          color: #374151;
        }

        .dcv-modalBtn--danger {
          background: #ef4444;
          color: #ffffff;
        }
      `}</style>

      {/* DESKTOP: Month grid + side agenda */}
      <div className="dcv-wrap dcv-desktop">
        <div className="dcv-card">
          <div className="dcv-head">
            <h3 className="dcv-title">
              <CalendarIcon size={16} />
              {monthLabel}
            </h3>
            <div className="dcv-controls">
              <button className="dcv-btnnav" onClick={goPrevMonth} aria-label="Previous month">
                <ChevronLeft size={16} />
              </button>
              <button className="dcv-btnnav" onClick={goNextMonth} aria-label="Next month">
                <ChevronRight size={16} />
              </button>
              <button className="dcv-today" onClick={goToday}>Today</button>
            </div>
          </div>

          <div className="dcv-grid" role="grid" tabIndex={0} ref={gridRef}>
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
              <div key={d} className="dcv-dow" role="columnheader">{d}</div>
            ))}
            {grid.map(({ date, inMonth }, idx) => {
              const iso = toISO(date);
              const apts = groupedAll.get(iso) || [];
              const isToday = iso === toISO(new Date());
              const isSelected = iso === selectedDateISO;
              const cellCls =
                "dcv-cell" +
                (!inMonth ? " dcv-cell--out" : "") +
                (isToday ? " dcv-cell--today" : "") +
                (isSelected ? " dcv-cell--sel" : "");
              const show = apts.slice(0, 2);
              const more = Math.max(0, apts.length - show.length);

              return (
                <div
                  key={idx}
                  className={cellCls}
                  role="gridcell"
                  aria-selected={isSelected ? "true" : "false"}
                  onClick={() => {
                    setSelectedDateISO(iso);
                    setActiveMonth(new Date(date.getFullYear(), date.getMonth(), 1));
                  }}
                  title={`${iso} — ${apts.length} appointment${apts.length === 1 ? "" : "s"}`}
                >
                  <div className="dcv-daybar">
                    <span className={"dcv-daynum" + (!inMonth ? " dcv-daynum--out" : "")}>{date.getDate()}</span>
                    {!!apts.length && <span className="dcv-count">{apts.length}</span>}
                  </div>
                  {!!apts.length && (
                    <div className="dcv-apts">
                      {show.map((a) => {
                        const t = statusTheme[a.status] || statusTheme.default;
                        return (
                          <div
                            key={a.id}
                            className="dcv-chip"
                            style={{ background: t.bg, color: t.fg, borderColor: t.bg }}
                            title={`${prettyTime(a.scheduledTime)} • ${a.patientName || "Patient"} • ${a.service || ""}`}
                          >
                            <span className="dcv-dot" style={{ background: dotColor(a.status) }} />
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                              {prettyTime(a.scheduledTime)} {a.patientName ? `• ${a.patientName}` : ""}
                            </span>
                          </div>
                        );
                      })}
                      {more > 0 && <span className="dcv-more">+{more} more</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="dcv-card">
          <div className="dcv-head">
            <h3 className="dcv-title">
              <Clock size={16} />
              {new Date(selectedDateISO + "T00:00:00").toLocaleDateString(undefined, {
                weekday: "long", month: "long", day: "numeric",
              })}
            </h3>
            <div className="dcv-controls" style={{ color: "#64748b", fontWeight: 800 }}>
              {(groupedAll.get(selectedDateISO) || []).length} scheduled
            </div>
          </div>

          {(groupedAll.get(selectedDateISO) || []).length === 0 ? (
            <div className="dcv-empty">No appointments for this day.</div>
          ) : (
            <div className="dcv-list">
              {agendaFor(selectedDateISO).map((a) => {
                const th = statusTheme[a.status] || statusTheme.default;
                return (
                  <article key={a.id} className="dcv-item">
                    <div className="dcv-avatar"><User size={16} /></div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <h4 className="dcv-name">{a.patientName || "Unknown Patient"}</h4>
                      <div className="dcv-meta">
                        <span className="dcv-chip2"><Clock size={12} />{prettyTime(a.scheduledTime)}</span>
                        <span className="dcv-chip2">{a.service || "Unknown Service"}</span>
                        <span className="dcv-badge" style={{ background: th.bg, color: th.fg, borderColor: th.bg }}>
                          {a.status || "pending"}
                        </span>
                      </div>

                      {a.notes && (
                        <div className="dcv-notes">
                          <strong style={{ color: "#475569" }}>Notes:</strong> {a.notes}
                        </div>
                      )}

                      <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#94a3b8", marginTop: 6 }}>
                        <FileText size={12} />
                        <span>
                          Created{" "}
                          {(() => {
                            const d = a.createdAt?.toDate?.() ?? a.createdAt ?? null;
                            try {
                              return d
                                ? new Intl.DateTimeFormat(undefined, {
                                    month: "short",
                                    day: "numeric",
                                  }).format(d)
                                : "—";
                            } catch {
                              return "—";
                            }
                          })()}
                        </span>
                      </div>
                    </div>

                    {/* Actions per status */}
                    {renderActions(a)}
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* MOBILE: 3-day compact view + list */}
      <div className="dcv-card dcv-mobile">
        <div className="mbar">
          <div style={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 8 }}>
            <CalendarIcon size={16} /> Agenda
          </div>
          <div className="mnav">
            <button
              className="mnav-btn"
              onClick={() => setSelectedDateISO(addDays(selectedDateISO, -1))}
              aria-label="Previous day"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              className="mnav-btn"
              onClick={() => setSelectedDateISO(toISO(new Date()))}
              aria-label="Today"
            >
              •
            </button>
            <button
              className="mnav-btn"
              onClick={() => setSelectedDateISO(addDays(selectedDateISO, 1))}
              aria-label="Next day"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* 3 tiny day cards */}
        <div className="mtrip">
          {mobileDays.map((iso) => {
            const d = new Date(iso + "T00:00:00");
            const list = agendaFor(iso);
            const isSel = iso === selectedDateISO;
            const firstFour = list.slice(0, 4);
            return (
              <button
                key={iso}
                className={`mcard ${isSel ? "mcard--sel" : ""}`}
                onClick={() => setSelectedDateISO(iso)}
              >
                <div className="mdate">
                  {d.toLocaleDateString(undefined, { weekday: "short" })} {d.getDate()}
                </div>
                <div className="mlegend">
                  {firstFour.map((a) => (
                    <span key={a.id} className="mdot" style={{ background: dotColor(a.status) }} />
                  ))}
                </div>
                <span className="mcount">{list.length}</span>
              </button>
            );
          })}
        </div>

        {/* Selected day list (mobile) */}
        <div className="mfoot">
          {new Date(selectedDateISO + "T00:00:00").toLocaleDateString(undefined, {
            weekday: "long",
            month: "short",
            day: "numeric",
          })}
        </div>

        {(groupedAll.get(selectedDateISO) || []).length === 0 ? (
          <div className="dcv-empty">No appointments for this day.</div>
        ) : (
          <div className="dcv-list" style={{ paddingTop: 0 }}>
            {agendaFor(selectedDateISO).map((a) => {
              const th = statusTheme[a.status] || statusTheme.default;
              return (
                <article key={a.id} className="dcv-item">
                  <div className="dcv-avatar"><User size={16} /></div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <h4 className="dcv-name">{a.patientName || "Unknown Patient"}</h4>
                    <div className="dcv-meta">
                      <span className="dcv-chip2"><Clock size={12} />{prettyTime(a.scheduledTime)}</span>
                      <span className="dcv-chip2">{a.service || "Unknown Service"}</span>
                      <span className="dcv-badge" style={{ background: th.bg, color: th.fg, borderColor: th.bg }}>
                        {a.status || "pending"}
                      </span>
                    </div>
                    {a.notes && (
                      <div className="dcv-notes">
                        <strong style={{ color: "#475569" }}>Notes:</strong> {a.notes}
                      </div>
                    )}
                  </div>

                  {/* Actions per status (mobile) */}
                  {renderActions(a)}
                </article>
              );
            })}
          </div>
        )}
      </div>

      {/* Decline reason modal */}
      {declineBooking && (
        <div className="dcv-modalOverlay" onClick={handleCancelDecline}>
          <div
            className="dcv-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h3 className="dcv-modalTitle">Decline appointment</h3>
            <p className="dcv-modalSubtitle">
              Please write a short reason. The patient may see this message in their portal/email.
            </p>

            <div className="dcv-modalBody">
              <div className="dcv-modalLabel">
                Appointment with <strong>{declineBooking.patientName}</strong>
              </div>
              <textarea
                className="dcv-modalTextarea"
                placeholder="Example: I’m not available at this time. Please reschedule for another day or choose a different time slot."
                value={declineReason}
                onChange={(e) => {
                  setDeclineReason(e.target.value);
                  setDeclineError("");
                }}
              />
              {declineError && (
                <div className="dcv-modalError">{declineError}</div>
              )}
            </div>

            <div className="dcv-modalActions">
              <button
                type="button"
                className="dcv-modalBtn dcv-modalBtn--secondary"
                onClick={handleCancelDecline}
              >
                Cancel
              </button>
              <button
                type="button"
                className="dcv-modalBtn dcv-modalBtn--danger"
                onClick={handleConfirmDecline}
              >
                Confirm decline
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default React.memo(DoctorCalendarView);
